import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import he from './locales/he.json';
import en from './locales/en.json';

export type AppLanguage = 'he' | 'en';

/**
 * Q-023 (owner, 2026-10-05): the UI is Hebrew unless the user explicitly chose
 * English. The browser / OS language is deliberately ignored. The explicit
 * choice is remembered under its own key, written only by the language switcher
 * (`setAppLanguage`). The older `ote_language` key was also written by the
 * browser-language detector, so it cannot tell a choice from a guess and is no
 * longer read.
 */
export const LANGUAGE_CHOICE_KEY = 'ote_language_choice';

/** The language to start in, from the stored explicit choice (Hebrew otherwise). */
export function initialLanguage(storedChoice: string | null | undefined): AppLanguage {
  return storedChoice === 'en' ? 'en' : 'he';
}

function readStoredChoice(): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(LANGUAGE_CHOICE_KEY);
  } catch {
    return null; // storage blocked (private mode): Hebrew
  }
}

/** Keep `<html lang dir>` in step with the language on every screen, /login included. */
function applyDocumentLanguage(lng: string) {
  if (typeof document === 'undefined') return;
  const lang = lng.split('-')[0] === 'en' ? 'en' : 'he';
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
}

// Registered before init so the initial language is applied too.
i18n.on('languageChanged', applyDocumentLanguage);

i18n
  .use(initReactI18next)
  .init({
    resources: {
      he: { translation: he },
      en: { translation: en },
    },
    lng: initialLanguage(readStoredChoice()),
    fallbackLng: 'he',
    // Only these two codes ever reach the app: code across the app compares
    // `i18n.language === 'he'`, and <html dir> follows the language.
    supportedLngs: ['he', 'en'],
    load: 'languageOnly',
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
  });

applyDocumentLanguage(i18n.language || 'he');

/** Switch the UI language as an explicit user choice (remembered on this device). */
export async function setAppLanguage(lang: AppLanguage): Promise<void> {
  try {
    localStorage.setItem(LANGUAGE_CHOICE_KEY, lang);
  } catch {
    /* storage unavailable: the switch still applies for this session */
  }
  await i18n.changeLanguage(lang);
}

export default i18n;
